import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getPublicacionesAdmin,
  getCategoriasAdmin,
  cambiarEstadoPublicacion,
  eliminarPublicacion,
} from '../../services/adminService';
import { useInstitucion } from '../../context/InstitucionContext';
import { useAuth } from '../../context/AuthContext';
import { idsCategoriasEnAlcance, esPublicador } from '../../utils/permisos';
import CategoriaSelect from '../../components/admin/CategoriaSelect';
import { mapaRutasCategorias } from '../../utils/categoriasArbol';

const AdminPublicaciones = () => {
  const { paleta: colors } = useInstitucion();
  const { usuario } = useAuth();

  const [publicaciones, setPublicaciones] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const rutasCategorias = useMemo(() => mapaRutasCategorias(categorias), [categorias]);

  // Un publicador sólo filtra dentro de su alcance (la API ya limita el listado).
  const idsFueraDeAlcance = useMemo(() => {
    const alcance = idsCategoriasEnAlcance(usuario, categorias);
    if (!alcance) return undefined;
    return new Set(categorias.filter((c) => !alcance.has(c.id)).map((c) => c.id));
  }, [usuario, categorias]);

  // Ruta abreviada (últimos dos niveles) para la columna Categoría; la completa va en el title
  const rutaCategoria = (pub) => {
    const ruta = rutasCategorias.get(pub.categoriaId);
    if (!ruta || ruta.length === 0) return { corta: pub.categoriaNombre, completa: pub.categoriaNombre };
    return { corta: ruta.slice(-2).join(' › '), completa: ruta.join(' › ') };
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPublicacionesAdmin(filtroCategoria || undefined);
      setPublicaciones(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filtroCategoria]);

  useEffect(() => {
    getCategoriasAdmin()
      .then(setCategorias)
      .catch(() => setCategorias([]));
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const handleToggleEstado = async (pub) => {
    try {
      await cambiarEstadoPublicacion(pub.id, !pub.activo);
      setPublicaciones((prev) =>
        prev.map((p) => (p.id === pub.id ? { ...p, activo: !p.activo } : p))
      );
    } catch (err) {
      alert(err.message);
    }
  };

  const handleEliminar = async (pub) => {
    if (!window.confirm(`¿Eliminar la publicación "${pub.titulo}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await eliminarPublicacion(pub.id);
      setPublicaciones((prev) => prev.filter((p) => p.id !== pub.id));
    } catch (err) {
      alert(err.message);
    }
  };

  const formatoFecha = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Publicaciones</h1>
          <p className="text-gray-500 text-sm mt-1">
            {esPublicador(usuario)
              ? 'Documentos de las categorías que tienes asignadas'
              : 'Gestiona los documentos publicados'}
          </p>
        </div>
        <Link
          to="/admin/publicaciones/nueva"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white font-semibold rounded-lg shadow-md transition-all hover:opacity-90"
          style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nueva publicación
        </Link>
      </div>

      {/* Filtro por categoría */}
      <div className="mb-4">
        <CategoriaSelect
          categorias={categorias}
          idsDeshabilitados={idsFueraDeAlcance}
          placeholder="Todas las categorías"
          value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': colors.primary }}
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">Cargando publicaciones...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-500">{error}</div>
        ) : publicaciones.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No hay publicaciones para mostrar.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-3 font-semibold">Título</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Categoría</th>
                  <th className="px-4 py-3 font-semibold hidden sm:table-cell">Fecha</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {publicaciones.map((pub) => (
                  <tr key={pub.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{pub.titulo}</div>
                      {pub.descripcion && (
                        <div className="text-gray-400 text-xs line-clamp-1">{pub.descripcion}</div>
                      )}
                    </td>
                    <td
                      className="px-4 py-3 text-gray-600 hidden md:table-cell"
                      title={rutaCategoria(pub).completa}
                    >
                      {rutaCategoria(pub).corta}
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{formatoFecha(pub.fechaPublicacion)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleEstado(pub)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          pub.activo
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                        title="Clic para cambiar el estado"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${pub.activo ? 'bg-green-500' : 'bg-gray-400'}`} />
                        {pub.activo ? 'Activa' : 'Inactiva'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/publicaciones/${pub.id}/editar`}
                          className="text-gray-500 hover:text-gray-900 p-1.5 rounded-md hover:bg-gray-100"
                          title="Editar"
                        >
                          <svg className="w-4.5 h-4.5" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>
                        <button
                          onClick={() => handleEliminar(pub)}
                          className="text-red-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50"
                          title="Eliminar"
                        >
                          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPublicaciones;
